import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calculator, DollarSign, TrendingDown, Truck, ArrowRight } from 'lucide-react';

export const InsuranceCalculator = ({ onGetStarted }) => {
  const [fleetSize, setFleetSize] = useState(10);
  const [avgMilesPerTrip, setAvgMilesPerTrip] = useState(15);
  const [tripsPerVehiclePerDay, setTripsPerVehiclePerDay] = useState(6);
  const [daysPerMonth, setDaysPerMonth] = useState(22);
  
  const [results, setResults] = useState(null);

  // Rates
  const PAYG_RATE_PER_MILE = 0.30;
  const TRADITIONAL_MONTHLY_PER_VEHICLE = 400; // Average traditional premium

  useEffect(() => {
    // Calculate totals
    const totalMilesPerMonth = fleetSize * avgMilesPerTrip * tripsPerVehiclePerDay * daysPerMonth;
    const paygCost = totalMilesPerMonth * PAYG_RATE_PER_MILE;
    const traditionalCost = fleetSize * TRADITIONAL_MONTHLY_PER_VEHICLE;
    const savings = traditionalCost - paygCost;
    const savingsPercent = Math.round((savings / traditionalCost) * 100);
    const annualSavings = savings * 12;

    setResults({
      totalMilesPerMonth,
      paygCost,
      traditionalCost,
      savings,
      savingsPercent,
      annualSavings
    });
  }, [fleetSize, avgMilesPerTrip, tripsPerVehiclePerDay, daysPerMonth]);

  return (
    <div className="glass-card p-6 lg:p-8">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-neon-green/20 flex items-center justify-center">
          <Calculator className="w-5 h-5 text-neon-green" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">Savings Calculator</h3>
          <p className="text-sm text-slate-400">See how much you could save</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Inputs */}
        <div className="space-y-6">
          <div>
            <label className="flex items-center justify-between text-sm text-slate-300 mb-2">
              <span>Fleet Size</span>
              <span className="text-neon-cyan font-medium">{fleetSize} vehicles</span>
            </label>
            <input
              type="range"
              min="1"
              max="100"
              value={fleetSize}
              onChange={(e) => setFleetSize(parseInt(e.target.value))}
              className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>1</span>
              <span>100</span>
            </div>
          </div>

          <div>
            <label className="flex items-center justify-between text-sm text-slate-300 mb-2">
              <span>Average Miles per Trip</span>
              <span className="text-neon-cyan font-medium">{avgMilesPerTrip} miles</span>
            </label>
            <input
              type="range"
              min="5"
              max="50"
              value={avgMilesPerTrip}
              onChange={(e) => setAvgMilesPerTrip(parseInt(e.target.value))}
              className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>5</span>
              <span>50</span>
            </div>
          </div>

          <div>
            <label className="flex items-center justify-between text-sm text-slate-300 mb-2">
              <span>Trips per Vehicle per Day</span>
              <span className="text-neon-cyan font-medium">{tripsPerVehiclePerDay} trips</span>
            </label>
            <input
              type="range"
              min="1"
              max="15"
              value={tripsPerVehiclePerDay}
              onChange={(e) => setTripsPerVehiclePerDay(parseInt(e.target.value))}
              className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>1</span>
              <span>15</span>
            </div>
          </div>

          <div>
            <label className="flex items-center justify-between text-sm text-slate-300 mb-2">
              <span>Operating Days per Month</span>
              <span className="text-neon-cyan font-medium">{daysPerMonth} days</span>
            </label>
            <input
              type="range"
              min="10"
              max="30"
              value={daysPerMonth}
              onChange={(e) => setDaysPerMonth(parseInt(e.target.value))}
              className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>10</span>
              <span>30</span>
            </div>
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-4">
            <div className="p-4 bg-white/5 rounded-lg">
              <div className="text-sm text-slate-400 mb-1">Monthly Miles</div>
              <div className="text-2xl font-bold text-white">
                {results.totalMilesPerMonth.toLocaleString()} mi
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="text-xs text-red-400 mb-1">Traditional Premium</div>
                <div className="text-xl font-bold text-red-400">
                  ${results.traditionalCost.toLocaleString()}/mo
                </div>
              </div>
              <div className="p-4 bg-neon-green/10 border border-neon-green/30 rounded-lg">
                <div className="text-xs text-neon-green mb-1">Pay As You Go</div>
                <div className="text-xl font-bold text-neon-green">
                  ${Math.round(results.paygCost).toLocaleString()}/mo
                </div>
              </div>
            </div>

            <motion.div 
              className="p-6 bg-gradient-to-r from-neon-green/20 to-neon-cyan/20 border border-neon-green/30 rounded-lg text-center"
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              key={results.savings}
            >
              <div className="flex items-center justify-center space-x-2 mb-2">
                <TrendingDown className="w-5 h-5 text-neon-green" />
                <span className="text-sm text-neon-green">Your Estimated Savings</span>
              </div>
              <div className="text-4xl font-heading font-bold text-white mb-1">
                ${Math.round(results.savings).toLocaleString()}<span className="text-lg text-slate-400">/mo</span>
              </div>
              <div className="text-sm text-slate-400">
                <span className="text-neon-green font-semibold">{results.savingsPercent}% less</span> than traditional insurance
              </div>
              <div className="mt-3 text-lg font-semibold text-neon-cyan">
                ${Math.round(results.annualSavings).toLocaleString()} saved per year
              </div>
            </motion.div>

            {onGetStarted && (
              <button
                onClick={onGetStarted}
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                <span>Get Started</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      <p className="text-xs text-slate-500 mt-6 text-center">
        * Estimates based on activity-based insurance model vs traditional flat premium per vehicle. 
        Actual rates may vary based on location, coverage, and claims history.
      </p>
    </div>
  );
};

export default InsuranceCalculator;
